// /ac-calculator: what size AC does this room need, and which ACs fit.
//
// Two screens on one route:
//   • No query string: the banner and the questions, with a Calculate button
//     (sticky on phones, like the product page's buy bar).
//   • With a query string: the result. The answers fold into a one-line
//     summary with an Edit button; the answer and the ACs come first.
// The answers live in the URL, so a result can be shared, the browser's Back
// button returns to the questions, and the canonical stays /ac-calculator
// (Canonical.jsx drops the query string).
//
// Sizing: lib/heatLoad.js (Daikin's heat-load sheet, line for line) for the
// city's hottest afternoon and its monsoon, with the rooms around taken as the
// average case; then only ACs that still cover that load after losing power
// to the heat (5% per °C above their series' full-power temperature). City
// data: data/indiaClimate.js. Which ACs: lib/acRecommend.js, from the live
// catalogue, prices and stock. The words, the guides and the FAQ:
// data/acCalculatorContent.js, shared with scripts/prerender.js so the HTML
// Google reads matches this page.
//
// Look (Oct 2026, v3): the shop's banner slot with its own banner, picture
// tiles for the questions, a blue answer card with a "which ACs keep up" chart,
// and colour-coded guide tables. Same blue, type and cards as the rest of the site.

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Bed, Sofa, Laptop, CookingPot, ShoppingBag, Building2, Cloud, Sunrise, Sun, Sunset, Thermometer,
  MapPin, Zap, ClipboardCheck, House, Wind, Check, ChartLine, IndianRupee, Lightbulb, CloudRain, Gauge,
} from 'lucide-react';
import Helmet from '../lib/Helmet';
import * as HL from '../lib/heatLoad';
import { CITY_CHOICES, NCR_CITIES, INDIA_CITIES, STATIONS, cityFor, tariffFor, BUSINESS_ROOMS } from '../data/indiaClimate';
import ProductCard from '../components/ProductCard';
import CredentialRibbon from '../components/CredentialRibbon';
import { formatINR, whatsAppLink } from '../lib/quote';
import { addToCart, openCart } from '../lib/cart';
import { IMG_FALLBACK } from '../lib/images';
import { track, FUNNEL } from '../lib/track';
import { BUSINESS } from '../data/pricing';
import { leadTimeFor } from '../lib/listing';

// "On order, 5 to 7 days", or sooner while NEXT_BATCH in data/pricing.js says so.
const onOrder = (v) => `On order, ${v?.model ? leadTimeFor(v.model.id, v.kit) : '5 to 7 days'}`;
import {
  ENGINE, UNITS, SIZES, TYPE_LABEL, recommend, collectionFor, heatDrivers, sheetLines,
} from '../lib/acRecommend';
import { CONSULT_SERVICES } from '../data/consultation';
import {
  CALC_SEO, GUIDE_COLUMNS, CITY_GUIDE_NOTE, guideNote, howItWorks, sizeGuide, cityGuide, calcFaq,
  tonLabel, degC, listSeries,
} from '../data/acCalculatorContent';

const { ROOM_TYPES } = HL;
const STATION_COUNT = Object.keys(STATIONS).length;

// The banner, in the same slot and shape as the shop pages' banners.
// The 900 and 1400 copies are made by scripts/resize-images.js.
const BANNER = {
  src: '/banner-ac-calculator.webp',
  srcSet: '/banner-ac-calculator-900.webp 900w, /banner-ac-calculator-1400.webp 1400w, /banner-ac-calculator.webp 2170w',
  width: 2170,
  height: 725,
  alt: 'AC calculator: find the right AC for your space. Enter room details, get the accurate AC capacity and see recommended Daikin models.',
};

// ─── choices ──────────────────────────────────────────────────────────────

const ROOMS = [
  ['bedroom', 'Bedroom', Bed], ['living', 'Living room', Sofa], ['study', 'Home office', Laptop],
  ['kitchen', 'Kitchen', CookingPot], ['shop', 'Shop', ShoppingBag], ['office', 'Office', Building2],
];
const ROOM_LABEL = Object.fromEntries(ROOMS.map(([k, label]) => [k, label]));
// Which of the three floors in the little building lights up: 0 is the top.
const FLOORS = [['ground', 'Ground', 2], ['mid', 'Middle', 1], ['top', 'Top floor', 0]];
const FLOOR_WORDS = { ground: 'Ground floor', mid: 'Middle floor', top: 'Top floor' };
// [key, label, short label for four tiles across a phone, icon, icon colour]
const SUNS = [
  ['N', 'Little or none', 'Little', Cloud, 'text-gray-500'],
  ['E', 'Morning', 'Morning', Sunrise, 'text-amber-500'],
  ['S', 'Midday', 'Midday', Sun, 'text-amber-500'],
  ['W', 'Afternoon', 'Afternoon', Sunset, 'text-orange-600'],
];
const SUN_WORDS = { N: 'Little sun', E: 'Morning sun', S: 'Midday sun', W: 'Afternoon sun' };
const CITY_KEYS = new Set(CITY_CHOICES.map((c) => c.key));
const HOURS = [4, 8, 12, 16];

const DEFAULTS = {
  mode: 'quick', room: 'bedroom', l: '', w: '', floor: '', sun: '',
  people: ROOM_TYPES.bedroom.people, city: 'delhi', hours: 8, heating: false,
  // detailed mode; '' = use the room type's default
  ch: 10, t: 24, roof: '', glass: '', ga: '', lw: '', eq: '', sf: 10, rate: '',
};
const DETAILED_KEYS = ['ch', 't', 'roof', 'glass', 'ga', 'lw', 'eq', 'sf', 'rate'];

const num = (v, d, lo, hi) => {
  const n = parseFloat(v);
  return Math.min(hi, Math.max(lo, Number.isFinite(n) ? n : d));
};

// ─── answers ⇄ URL ────────────────────────────────────────────────────────

const PARAM_KEYS = ['room', 'l', 'w', 'floor', 'sun', 'people', 'city', 'hours', 'heating', 'mode', ...DETAILED_KEYS];

function readParams(params) {
  const room = params.get('room');
  const l = parseFloat(params.get('l'));
  const w = parseFloat(params.get('w'));
  const floor = params.get('floor');
  const sun = params.get('sun');
  if (!ROOM_LABEL[room] || !(l > 0) || !(w > 0) || !FLOOR_WORDS[floor] || !SUN_WORDS[sun]) return null;
  const city = CITY_KEYS.has(params.get('city')) ? params.get('city') : 'delhi';
  const get = (k, d) => (params.has(k) ? params.get(k) : d);
  const t = ROOM_TYPES[room];
  return {
    ...DEFAULTS,
    room, l: String(num(l, 12, 4, 100)), w: String(num(w, 12, 4, 100)), floor, sun, city,
    people: Math.round(num(get('people', t.people), t.people, 1, 40)),
    hours: HOURS.includes(Number(get('hours', 8))) ? Number(get('hours', 8)) : 8,
    heating: get('heating', '0') === '1',
    mode: get('mode', 'quick') === 'detailed' ? 'detailed' : 'quick',
    ch: get('ch', DEFAULTS.ch), t: get('t', DEFAULTS.t), roof: get('roof', '') === '1' ? '1' : '', glass: get('glass', ''),
    ga: get('ga', ''), lw: get('lw', ''), eq: get('eq', ''), sf: get('sf', DEFAULTS.sf), rate: get('rate', ''),
  };
}

function toParams(a) {
  const out = new URLSearchParams();
  PARAM_KEYS.forEach((k) => {
    if (a.mode !== 'detailed' && DETAILED_KEYS.includes(k)) return;
    const v = k === 'heating' ? (a.heating ? '1' : '0') : a[k];
    if (v === '' || v == null) return;
    out.set(k, String(v));
  });
  return out.toString();
}

// ─── the sum ──────────────────────────────────────────────────────────────

function compute(a) {
  const city = cityFor(a.city);
  const t = ROOM_TYPES[a.room] || ROOM_TYPES.bedroom;
  const detailed = a.mode === 'detailed';
  const l = num(a.l, 12, 4, 100);
  const w = num(a.w, 12, 4, 100);
  const area = Math.round(l * w);
  const height = detailed ? num(a.ch, 10, 8, 25) : 10;
  const targetTemp = detailed ? num(a.t, 24, 18, 28) : 24;

  const extra = {};
  if (detailed) {
    if (a.glass) extra.glassType = a.glass;
    if (a.ga !== '' && a.ga != null) extra.glassArea = num(a.ga, 0, 0, 500);
    if (a.lw !== '' && a.lw != null) extra.lightW = num(a.lw, t.lightW, 0, 10);
    if (a.eq !== '' && a.eq != null) extra.eqKW = num(a.eq, t.eqKW, 0, 100);
    extra.safety = num(a.sf, 10, 0, 20);
    extra.roofInsulated = a.roof === '1';
  }

  const climate = HL.climateFor(city.station);
  const room = HL.roomFor({ type: a.room, l, w, height, floor: a.floor, sun: a.sun, people: a.people, ...extra });
  const need = HL.needFor(room, climate, HL.insideAt(targetTemp));
  const defaultRate = tariffFor(a.room);
  const rate = detailed && a.rate !== '' && a.rate != null ? num(a.rate, defaultRate, 1, 20) : defaultRate;
  const rec = recommend(need, {
    mo: city.mo, rate, hours: a.hours, targetTemp, floor: a.floor, sun: a.sun, heating: a.heating,
  });
  return { need, climate, room, rec, rate, city, l, w, area, detailed, targetTemp };
}

// Which season sets the size, and the outdoor temperature and load that go with it.
function governing(need) {
  return need.monsoonTR > need.summerTR
    ? { season: 'monsoon', tempC: need.monsoonC, loadTR: need.monsoonTR, words: 'on a humid monsoon day', short: 'in the monsoon' }
    : { season: 'summer', tempC: need.peakC, loadTR: need.summerTR, words: `on a ${degC(need.peakC)} afternoon`, short: `at ${degC(need.peakC)}` };
}

// The bars of "Which ACs keep up": one size below the answer, the answer (split
// into the models that keep up and those that don't), and the next size when
// it is offered too. Each bar is what the AC really gives at the heat.
function keepUpRows(res) {
  const n = res.need;
  const r = res.rec;
  const gov = governing(n);
  const at = (u) => HL.capacityTR(u.kw, u.full, gov.tempC);
  let sizes;
  if (r.mode === 'single') {
    const i = SIZES.indexOf(r.size);
    const hi = r.ans.next != null ? SIZES.indexOf(r.ans.next) : i;
    sizes = SIZES.slice(Math.max(0, i - 1), hi + 1);
  } else {
    sizes = [SIZES[SIZES.length - 1]];
  }
  const rows = [];
  sizes.forEach((s) => {
    const us = UNITS.filter((u) => u.soldAs === s);
    const ok = us.filter((u) => HL.fits(u, n));
    const short = us.filter((u) => !ok.includes(u));
    if (ok.length && short.length) {
      const fShort = [...new Set(short.map((u) => u.full))];
      const fOk = [...new Set(ok.map((u) => u.full))];
      const byHeat = fShort.length === 1 && fOk.length === 1 && fShort[0] !== fOk[0];
      rows.push({ key: `${s}-short`, label: byHeat ? `${tonLabel(s)}, rated ${fShort[0]}°C` : `${tonLabel(s)}: ${listSeries(short)}`, tr: Math.max(...short.map(at)), ok: false, why: 'falls short' });
      rows.push({ key: `${s}-ok`, label: byHeat ? `${tonLabel(s)}, rated ${fOk[0]}°C` : `${tonLabel(s)}: ${listSeries(ok)}`, tr: Math.min(...ok.map(at)), ok: true });
    } else if (ok.length) {
      rows.push({ key: `${s}`, label: tonLabel(s), tr: Math.min(...ok.map(at)), ok: true });
    } else {
      rows.push({ key: `${s}`, label: tonLabel(s), tr: Math.max(...us.map(at)), ok: false, why: 'too small' });
    }
  });
  if (r.mode === 'pair') {
    const tr = r.pair.units.reduce((sum, v) => {
      const u = UNITS.find((x) => x.id === v.model.id);
      return sum + HL.capacityTR(u.kw, u.full, gov.tempC);
    }, 0);
    rows.push({ key: 'pair', label: `Two ACs: ${tonLabel(r.pair.sizes[0])} + ${tonLabel(r.pair.sizes[1])}`, tr, ok: true });
  }
  const top = Math.max(gov.loadTR, ...rows.map((x) => x.tr)) * 1.08;
  return {
    gov,
    linePct: (gov.loadTR / top) * 100,
    rows: rows.map((x) => ({ ...x, pct: Math.max(3, (x.tr / top) * 100) })),
  };
}

// Guide size chips: lighter blue for smaller ACs, darker for bigger.
const SIZE_CHIP = {
  0.8: 'bg-blue-50 text-blue-900', 1: 'bg-blue-100 text-blue-900', 1.5: 'bg-blue-300 text-blue-950',
  1.8: 'bg-blue-500 text-white', 2: 'bg-blue-700 text-white', 2.5: 'bg-blue-900 text-white',
};
const sizeChip = (size) => SIZE_CHIP[size] || 'bg-gray-900 text-white';

// ─── small pieces, in the site's own styles ───────────────────────────────

function Pill({ on, onClick, children }) {
  return (
    <button type="button" aria-pressed={on} onClick={onClick}
      className={`rounded-full px-4 py-2.5 text-[13px] leading-none transition ${
        on ? 'bg-blue-600 font-medium text-white' : 'border border-gray-300 text-gray-600 hover:border-blue-400 hover:text-blue-600'
      }`}>
      {children}
    </button>
  );
}

// A picture tile: room type, floor or sun. The selected one is outlined in blue.
function Tile({ on, onClick, label, className = '', children }) {
  return (
    <button type="button" aria-pressed={on} onClick={onClick}
      className={`flex min-w-0 flex-col items-center justify-center gap-1.5 rounded-xl border px-1 py-3 text-xs transition ${
        on ? 'border-blue-600 bg-blue-50 font-semibold text-blue-700 ring-1 ring-blue-600' : 'border-gray-200 bg-white font-medium text-gray-700 hover:border-blue-300'
      } ${className}`}>
      {children}
      <span className="max-w-full truncate">{label}</span>
    </button>
  );
}

// Three floors of a building, the chosen one lit; the roof is orange on a top floor.
function FloorIcon({ lit, on }) {
  return (
    <span aria-hidden="true" className="flex w-8 flex-col gap-0.5">
      {[0, 1, 2].map((i) => (
        <span key={i} className={`h-2 rounded-sm ${
          i === lit ? (lit === 0 && on ? 'bg-orange-500' : 'bg-blue-600') : on ? 'bg-blue-200' : 'bg-gray-200'
        }`} />
      ))}
    </span>
  );
}

function Step({ n, title, hint, hintClass = 'text-gray-500', children }) {
  return (
    <fieldset className="min-w-0">
      <legend className="flex items-center gap-2 text-sm font-semibold text-gray-900">
        <span className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs font-semibold text-white">{n}</span>
        {title}
      </legend>
      <div className="mt-2.5">{children}</div>
      {hint && <p className={`mt-2 text-xs ${hintClass}`}>{hint}</p>}
    </fieldset>
  );
}

const inputCls = 'h-11 w-full rounded-lg border border-gray-300 px-3 text-base text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500';

function Field({ id, label, children }) {
  return (
    <div className="min-w-0">
      <label htmlFor={id} className="block text-xs text-gray-600">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

const ChatIcon = ({ className = 'h-5 w-5' }) => (
  <svg viewBox="0 0 24 24" className={`shrink-0 ${className}`} fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z" />
  </svg>
);
const PhoneIcon = ({ className = 'h-4 w-4' }) => (
  <svg viewBox="0 0 24 24" className={`shrink-0 ${className}`} fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.5 2.1L8 9.8a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.7.7a2 2 0 0 1 1.7 2z" />
  </svg>
);

// WhatsApp (solid green) and call (blue outline): the two ways to reach the team.
function TeamButtons({ waHref, onWhatsApp }) {
  return (
    <div className="flex flex-wrap gap-2">
      <a href={waHref} target="_blank" rel="noopener noreferrer" onClick={onWhatsApp}
        className="flex h-11 flex-1 basis-36 items-center justify-center gap-1.5 whitespace-nowrap rounded-xl bg-green-600 text-[13px] font-semibold text-white hover:bg-green-700">
        <ChatIcon className="h-[18px] w-[18px]" /> WhatsApp us
      </a>
      <a href={`tel:${BUSINESS.phone}`} onClick={() => track(FUNNEL.PHONE_CLICK, { source: 'calculator' })}
        className="flex h-11 flex-1 basis-36 items-center justify-center gap-1.5 whitespace-nowrap rounded-xl border-2 border-blue-700 bg-white text-[13px] font-semibold text-blue-700">
        <PhoneIcon /> Call {BUSINESS.phoneDisplay.replace('+91 ', '')}
      </a>
    </div>
  );
}

// Add one AC to the cart and open it (in stock), or go to the request page.
function useBuy(source) {
  const navigate = useNavigate();
  return (v) => {
    if (!v.inStock) {
      navigate(`/request-unit?model=${v.model.slug}&kit=${v.kit}`);
      return;
    }
    addToCart(v.model.id, v.kit, 1);
    track(FUNNEL.ADD_TO_CART, { model: v.model.id, kit: v.kit, qty: 1, value: v.price, source });
    openCart();
  };
}

const productHref = (v) => `/${v.model.slug}?kit=${v.kit}`;

// One column of "Compare all three": photo, kind, price, power a year, the
// 5-year total, when it is the best buy, then Add to cart and Know more.
function CompareCol({ v, size }) {
  const best = v.recommended;
  const buy = useBuy('calculator_compare');
  // "Best if you run it about 8 hrs a day": left out when it wins at every use.
  const bestLine = v.type === 'heat' ? 'Best if you want heating too'
    : v.bestFor && v.bestFor !== 'any number of hours' ? `Best if you run it ${v.bestFor}` : '';
  return (
    <div className={`relative flex min-w-0 flex-col rounded-xl px-2 pb-2 pt-3.5 text-center ${
      best ? 'border-2 border-green-600 bg-green-50' : 'border border-gray-200 bg-white'
    }`}>
      {best && (
        <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-green-600 px-2 py-0.5 text-[10px] font-extrabold text-white">
          BEST BUY
        </span>
      )}
      <Link to={productHref(v)} className="block">
        <img src={v.model.image} alt="" width="160" height="80" loading="lazy"
          onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = IMG_FALLBACK; }}
          className="mx-auto h-14 w-full object-contain sm:h-16" />
        <p className="mt-1.5 text-[13px] font-extrabold text-gray-900 sm:text-sm">{v.typeLabel.replace(' inverter', '')}</p>
        {v.size !== size && <p className="text-[10px] font-semibold text-gray-500">{tonLabel(v.size)}</p>}
      </Link>
      <dl className="mt-2 flex flex-1 flex-col gap-1.5 text-left text-[11px] sm:text-xs">
        <div><dt className="text-gray-500">Price</dt><dd className="text-[13px] font-bold text-gray-900">{formatINR(v.price)}</dd></div>
        <div><dt className="text-gray-500">Power a year</dt><dd className="text-[13px] font-bold text-gray-900">{formatINR(v.yearly)}</dd></div>
        <div className="border-t border-gray-200 pt-1.5">
          <dt className="text-gray-500">5-year total</dt>
          <dd className={`text-sm font-extrabold ${best ? 'text-green-700' : 'text-gray-900'}`}>{formatINR(v.fiveYear)}</dd>
        </div>
      </dl>
      {!v.inStock && <p className="mt-1.5 text-[10px] font-semibold text-orange-700">{onOrder(v)}</p>}
      {bestLine && (
        <p className="mt-2 rounded-lg bg-white/70 px-1 py-1.5 text-[10px] font-semibold leading-snug text-gray-700 ring-1 ring-gray-200 sm:text-[11px]">
          {bestLine}
        </p>
      )}
      <button type="button" onClick={() => buy(v)}
        className="mt-2 h-9 w-full rounded-lg bg-blue-600 text-[12px] font-bold text-white hover:bg-blue-700 sm:text-[13px]">
        {v.inStock ? 'Add to cart' : 'Request'}
      </button>
      <Link to={productHref(v)}
        className="mt-1.5 flex h-9 w-full items-center justify-center rounded-lg border border-blue-600 bg-white text-[12px] font-semibold text-blue-700 hover:bg-blue-50 sm:text-[13px]">
        Know more
      </Link>
    </div>
  );
}

// Both ACs of a pair into the cart, then open it. Only called when both are in stock.
function addPairToCart(pair, source) {
  pair.units.forEach((v) => {
    addToCart(v.model.id, v.kit, 1);
    track(FUNNEL.ADD_TO_CART, { model: v.model.id, kit: v.kit, qty: 1, value: v.price, source });
  });
  openCart();
}

const capAtPeak = (v, need) => {
  const u = UNITS.find((x) => x.id === v.model.id);
  return u ? HL.capacityTR(u.kw, u.full, need.peakC) : 0;
};

// A big room: two ACs as the best buy, in the same look as the single best buy.
function PairBuy({ pair, need, hours, near }) {
  const buy = useBuy('calculator_pair');
  const gives = pair.units.reduce((t, v) => t + capAtPeak(v, need), 0);
  const reasons = [
    near
      ? `The biggest single split AC, ${tonLabel(near.size)}, gives ${near.givesTR.toFixed(2)} TR ${near.season === 'summer' ? `at ${degC(need.peakC)}` : 'in the monsoon'}; this room needs ${near.needTR.toFixed(2)} TR.`
      : `No single split AC keeps up with this room at ${degC(need.peakC)}.`,
    'Two ACs on different walls spread the cooling evenly across a room this size.',
    `About ${formatINR(pair.yearly)} a year in electricity for both, at ${hours === 16 ? '16+' : hours} hrs a day.`,
  ];
  return (
    <section aria-label="Best buy for you" className="rounded-2xl border-2 border-green-600 bg-white p-4 shadow-sm">
      <span className="inline-block rounded-full bg-green-600 px-2.5 py-1 text-[11px] font-extrabold tracking-wide text-white">BEST BUY FOR YOU</span>
      <p className="mt-3 text-[15px] font-bold text-gray-900">Two ACs on different walls</p>
      <ul className="mt-2.5 flex flex-col gap-2.5">
        {pair.units.map((v, i) => (
          <li key={v.key + i} className="flex items-center gap-3">
            <img src={v.model.image} alt="" width="72" height="56" loading="eager"
              onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = IMG_FALLBACK; }}
              className="h-14 w-[72px] shrink-0 rounded-lg bg-gray-50 object-contain p-1" />
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-semibold leading-snug text-gray-900">{tonLabel(v.size)} {TYPE_LABEL[v.type]}</p>
              <Link to={productHref(v)} className="text-xs font-semibold text-blue-700">Know more ›</Link>
              {!v.inStock && <p className="text-[11px] font-semibold text-orange-700">{onOrder(v)}</p>}
            </div>
            <div className="flex shrink-0 flex-col items-end gap-1">
              <p className="text-[15px] font-extrabold text-gray-900">{formatINR(v.price)}</p>
              {!pair.inStock && (
                <button type="button" onClick={() => buy(v)}
                  className="h-8 rounded-lg border border-blue-600 px-2.5 text-xs font-semibold text-blue-700">
                  {v.inStock ? 'Add' : 'Request'}
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
      <div className="mt-3 flex items-baseline justify-between border-t border-gray-200 pt-2.5">
        <span className="text-[13px] text-gray-600">For both</span>
        <span className="text-2xl font-extrabold text-gray-900">{formatINR(pair.price)}</span>
      </div>
      <p className="mt-2 text-sm font-medium leading-snug text-gray-900">
        Together they give {gives.toFixed(2)} TR at {degC(need.peakC)}, enough for this room on the hottest afternoon.
      </p>
      <div className="mt-2.5 flex flex-wrap gap-1.5">
        <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2.5 py-1 text-[11px] font-semibold text-green-800">
          <Check className="h-3 w-3" strokeWidth={3} aria-hidden="true" /> Keeps up at {degC(need.peakC)}
        </span>
        {pair.inStock ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-1 text-[11px] font-semibold text-blue-700">
            <Zap className="h-3 w-3" aria-hidden="true" /> Both in stock · 24-hr delivery
          </span>
        ) : (
          <span className="rounded-full bg-orange-50 px-2.5 py-1 text-[11px] font-semibold text-orange-700">One is on order, 5 to 7 days</span>
        )}
      </div>
      {pair.inStock && (
        <button type="button" onClick={() => addPairToCart(pair, 'calculator_pair')}
          className="mt-3.5 h-12 w-full rounded-xl bg-blue-600 text-[15px] font-bold text-white hover:bg-blue-700">
          Add both to cart
        </button>
      )}
      <details className="group mt-3 border-t border-gray-100 pt-2">
        <summary className="flex cursor-pointer list-none items-center justify-between py-1.5 text-[13px] font-semibold text-blue-700 [&::-webkit-details-marker]:hidden">
          Why two ACs?
          <svg viewBox="0 0 20 20" className="h-4 w-4 shrink-0 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M5 8l5 5 5-5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </summary>
        <ul className="mt-1.5 space-y-1.5 pb-1">
          {reasons.map((t) => (
            <li key={t} className="flex gap-1.5 text-[13px] leading-snug text-gray-700">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-green-600" strokeWidth={2.6} aria-hidden="true" />
              <span>{t}</span>
            </li>
          ))}
        </ul>
      </details>
    </section>
  );
}

// The biggest single AC when it falls only a little short: shown with the truth
// about it, so the customer can choose it knowingly.
function NearSingle({ v, need }) {
  const buy = useBuy('calculator_near_single');
  const where = v.season === 'summer' ? `at ${degC(need.peakC)}` : 'in the monsoon';
  return (
    <section aria-label="Or one bigger AC">
      <h2 className="text-base font-bold text-gray-900">Or one bigger AC</h2>
      <div className="mt-2.5 rounded-2xl border border-gray-200 bg-white p-3.5">
        <div className="flex items-center gap-3">
          <img src={v.model.image} alt="" width="72" height="56" loading="lazy"
            onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = IMG_FALLBACK; }}
            className="h-14 w-[72px] shrink-0 rounded-lg bg-gray-50 object-contain p-1" />
          <div className="min-w-0 flex-1">
            <p className="text-[13px] font-semibold leading-snug text-gray-900">{tonLabel(v.size)} {v.typeLabel}</p>
            <p className={`text-[11px] font-semibold ${v.inStock ? 'text-blue-700' : 'text-orange-700'}`}>
              {v.inStock ? 'In stock · 24-hr delivery' : onOrder(v)}
            </p>
          </div>
          <p className="shrink-0 text-[15px] font-extrabold text-gray-900">{formatINR(v.price)}</p>
        </div>
        <div className="mt-3 rounded-xl bg-orange-50 px-3 py-2.5">
          <p className="text-[13px] font-bold text-orange-900">
            {v.shortPct}% short {v.season === 'summer' ? 'on the hottest afternoons' : 'in humid monsoon weather'}
          </p>
          <p className="mt-1 text-xs leading-relaxed text-orange-900">
            It gives {v.givesTR.toFixed(2)} TR {where}; this room needs {v.needTR.toFixed(2)} TR.
            {v.season === 'summer' && (v.shortAboveC
              ? ` Above about ${v.shortAboveC}°C the room may run a little warmer than you set it.`
              : ' On the hottest afternoons the room may run a little warmer than you set it.')}
            {v.season === 'summer' && v.monsoonOk && ' The rest of the year, including the monsoon, it cools the room fine.'}
          </p>
        </div>
        <div className="mt-3 flex gap-2">
          <button type="button" onClick={() => buy(v)}
            className="h-11 flex-[3] rounded-xl bg-blue-600 text-sm font-bold text-white hover:bg-blue-700">
            {v.inStock ? 'Add to cart' : 'Request this AC'}
          </button>
          <Link to={productHref(v)}
            className="flex h-11 flex-[2] items-center justify-center rounded-xl border-2 border-blue-600 bg-white text-sm font-semibold text-blue-700 hover:bg-blue-50">
            Know more
          </Link>
        </div>
      </div>
    </section>
  );
}

// "Not sure? Talk to an HVAC engineer": the paid consultation, from the
// result. A room that needs a central system gets end-to-end HVAC design
// (site visit, heat load, drawing, BOQ); every other result the ₹99 call.
function ConsultCard({ mode, area, type }) {
  const call = CONSULT_SERVICES.find((x) => x.id === 'call');
  const design = CONSULT_SERVICES.find((x) => x.id === 'design');
  if (mode === 'team' && design) {
    return (
      <section className="rounded-2xl border border-blue-200 bg-blue-50 p-4">
        <p className="text-[15px] font-bold text-gray-900">Get it designed: site visit, drawing and BOQ</p>
        <p className="mt-1 text-xs leading-relaxed text-gray-700">
          Our engineer visits, works out the heat load, and gives you an HVAC layout drawing and a bill of quantities.
          ₹{design.rates[type] || design.rates.home} a sq ft, adjusted against your order.
        </p>
        <Link to={`/hvac-consultant?service=design&type=${type}&sqft=${area}&from=calculator`}
          className="mt-3 flex h-11 items-center justify-center rounded-xl bg-blue-700 text-sm font-bold text-white hover:bg-blue-800">
          Book HVAC design
        </Link>
      </section>
    );
  }
  if (!call) return null;
  return (
    <section className="rounded-2xl border border-blue-200 bg-blue-50 p-4">
      <p className="text-[15px] font-bold text-gray-900">Not sure? Talk to an HVAC engineer</p>
      <p className="mt-1 text-xs leading-relaxed text-gray-700">
        A 30-minute video call.{mode === 'pair' ? ' For a room this size we may suggest one cassette or ductable unit instead.' : ' Fee adjusted against your order.'}
      </p>
      <div className="mt-3 flex items-center gap-3">
        <p className="flex-1">
          <span className="text-xl font-extrabold text-gray-900">{formatINR(call.price)}</span>
          {call.was && <s className="ml-1.5 text-xs text-gray-500">{formatINR(call.was)}</s>}
        </p>
        <Link to="/hvac-consultant?service=call&from=calculator"
          className="flex h-11 items-center rounded-xl bg-blue-700 px-5 text-sm font-bold text-white hover:bg-blue-800">
          Book a call
        </Link>
      </div>
    </section>
  );
}

// The one AC we recommend: photo, price, one line of why, Add to cart.
// The full reasons fold under "Why this one?".
function BestBuy({ r, peakC }) {
  const v = r.rec;
  const buy = useBuy('calculator_best_buy');
  return (
    <section aria-label="Best buy for you" className="rounded-2xl border-2 border-green-600 bg-white p-4 shadow-sm">
      <span className="inline-block rounded-full bg-green-600 px-2.5 py-1 text-[11px] font-extrabold tracking-wide text-white">BEST BUY FOR YOU</span>
      <div className="mt-3 flex items-center gap-3">
        <img src={v.model.image} alt={v.model.title} width="96" height="96" loading="eager"
          onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = IMG_FALLBACK; }}
          className="h-24 w-24 shrink-0 rounded-xl bg-gray-50 object-contain p-1" />
        <div className="min-w-0">
          <Link to={productHref(v)} className="text-[15px] font-bold leading-snug text-gray-900 hover:text-blue-700">{v.model.title}</Link>
          {r.sizeNote && <p className="mt-0.5 text-[11px] text-gray-500">{r.sizeNote}</p>}
          <p className="mt-1 flex flex-wrap items-baseline gap-x-2">
            <span className="text-2xl font-extrabold text-gray-900">{formatINR(v.price)}</span>
            {v.mrp > v.price && <s className="text-xs text-gray-500">{formatINR(v.mrp)}</s>}
          </p>
        </div>
      </div>
      <p className="mt-3 text-sm font-medium leading-snug text-gray-900">{r.short}</p>
      <div className="mt-2.5 flex flex-wrap gap-1.5">
        <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2.5 py-1 text-[11px] font-semibold text-green-800">
          <Check className="h-3 w-3" strokeWidth={3} aria-hidden="true" /> Cools fine at {degC(peakC)}
        </span>
        {v.inStock ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-1 text-[11px] font-semibold text-blue-700">
            <Zap className="h-3 w-3" aria-hidden="true" /> In stock · 24-hr delivery
          </span>
        ) : (
          <span className="rounded-full bg-orange-50 px-2.5 py-1 text-[11px] font-semibold text-orange-700">{onOrder(v)}</span>
        )}
      </div>
      <div className="mt-3.5 flex gap-2">
        <button type="button" onClick={() => buy(v)}
          className="h-12 flex-[3] rounded-xl bg-blue-600 text-[15px] font-bold text-white hover:bg-blue-700">
          {v.inStock ? 'Add to cart' : 'Request this AC'}
        </button>
        <Link to={productHref(v)}
          className="flex h-12 flex-[2] items-center justify-center rounded-xl border-2 border-blue-600 bg-white text-[15px] font-semibold text-blue-700 hover:bg-blue-50">
          Know more
        </Link>
      </div>
      <details className="group mt-3 border-t border-gray-100 pt-2">
        <summary className="flex cursor-pointer list-none items-center justify-between py-1.5 text-[13px] font-semibold text-blue-700 [&::-webkit-details-marker]:hidden">
          Why this one?
          <svg viewBox="0 0 20 20" className="h-4 w-4 shrink-0 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M5 8l5 5 5-5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </summary>
        <ul className="mt-1.5 space-y-1.5 pb-1">
          {r.reasons.map((t) => (
            <li key={t} className="flex gap-1.5 text-[13px] leading-snug text-gray-700">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-green-600" strokeWidth={2.6} aria-hidden="true" />
              <span>{t}</span>
            </li>
          ))}
        </ul>
      </details>
    </section>
  );
}

function StatTiles({ need, className = '' }) {
  return (
    <div className={`grid grid-cols-3 gap-2 ${className}`}>
      <div className="rounded-xl bg-blue-50 p-2.5">
        <p className="text-base font-bold tabular-nums text-blue-700 sm:text-lg">{need.loadTR.toFixed(2)} TR</p>
        <p className="text-[11px] text-gray-700">Heat load</p>
      </div>
      <div className="rounded-xl bg-orange-50 p-2.5">
        <p className="text-base font-bold tabular-nums text-orange-700 sm:text-lg">{degC(need.peakC)}</p>
        <p className="text-[11px] text-gray-700">Hottest afternoon</p>
      </div>
      <div className="rounded-xl bg-cyan-50 p-2.5">
        <p className="text-base font-bold tabular-nums text-cyan-700 sm:text-lg">{need.monsoonTR.toFixed(2)} TR</p>
        <p className="text-[11px] text-gray-700">Monsoon load</p>
      </div>
    </div>
  );
}

// "Which ACs keep up": a bar for each size, the room's load as a line.
function KeepUpChart({ chart }) {
  return (
    <div>
      <p className="text-[13px] font-bold text-gray-900">Which ACs keep up {chart.gov.short}?</p>
      <p className="mt-0.5 text-[11px] leading-snug text-gray-500">
        ACs lose 5% of their power for every °C above their rating. The dark line is your room.
      </p>
      <div className="mt-3 flex flex-col gap-2.5">
        {chart.rows.map((row) => (
          <div key={row.key}>
            <div className="flex items-baseline justify-between gap-2 text-xs">
              <span className="min-w-0 truncate font-semibold text-gray-900">{row.label}</span>
              <span className={`shrink-0 tabular-nums ${row.ok ? 'font-semibold text-green-700' : 'text-gray-500'}`}>
                {row.tr.toFixed(2)} TR · {row.ok ? 'keeps up' : row.why}
              </span>
            </div>
            <div className="relative mt-1 h-2.5 rounded-full bg-gray-100">
              <div className={`h-2.5 rounded-full ${row.ok ? 'bg-green-600' : 'bg-gray-300'}`} style={{ width: `${row.pct}%` }} />
              <div className="absolute -top-1 h-[18px] w-0.5 rounded bg-gray-900" style={{ left: `${chart.linePct}%` }} />
            </div>
          </div>
        ))}
      </div>
      <p className="mt-2 flex items-center gap-1.5 text-[11px] text-gray-700">
        <span aria-hidden="true" className="h-3 w-0.5 rounded bg-gray-900" />
        Your room needs {chart.gov.loadTR.toFixed(2)} TR
      </p>
    </div>
  );
}

// ─── the page ─────────────────────────────────────────────────────────────

export default function ACCalculator() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const search = params.toString();
  const applied = useMemo(() => readParams(new URLSearchParams(search)), [search]);
  const [form, setForm] = useState(() => applied || DEFAULTS);
  const [editing, setEditing] = useState(false);
  const [missing, setMissing] = useState([]);

  // Back and forward move between the questions and a result: keep the
  // questions in step with the URL.
  useEffect(() => {
    if (applied) setForm(applied);
    setEditing(false);
    setMissing([]);
  }, [applied]);

  const onResult = Boolean(applied);
  const showForm = !onResult || editing;
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const res = useMemo(() => (applied ? compute(applied) : null), [applied]);

  // The guides and FAQ: same engine, worked out once.
  const guide = useMemo(() => sizeGuide(ENGINE, UNITS), []);
  const cities = useMemo(() => cityGuide(ENGINE, UNITS), []);
  const faqs = useMemo(() => calcFaq(ENGINE, UNITS), []);
  const how = useMemo(() => howItWorks(ENGINE), []);
  const note = useMemo(() => guideNote(ENGINE), []);

  const calculate = () => {
    const gaps = [];
    if (!(parseFloat(form.l) > 0) || !(parseFloat(form.w) > 0)) gaps.push('room length and width');
    if (!form.floor) gaps.push('which floor');
    if (!form.sun) gaps.push('when sun falls on the window');
    setMissing(gaps);
    if (gaps.length) return;
    const r = compute(form);
    track(FUNNEL.SIZING_COMPLETE, {
      source: 'calculator', tr: r.need.loadTR, result: r.rec.mode === 'single' ? r.rec.size : r.rec.mode,
      room: form.room, area: r.area, mode: form.mode, city: form.city,
    });
    navigate(`/ac-calculator?${toParams(form)}`, { replace: onResult });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Hours and heating change the picks, not the size: they live on the result.
  const refine = (patch) => {
    const next = { ...applied, ...patch };
    navigate(`/ac-calculator?${toParams(next)}`, { replace: true });
  };

  const openEdit = () => {
    setEditing((e) => !e);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ── result values ──
  let answerBig = '';
  let answerShort = '';
  let answerMore = '';
  let answerSub = '';
  let waText = '';
  let chart = null;
  if (res) {
    const n = res.need;
    const gov = governing(n);
    const TR = `${n.loadTR.toFixed(2)} TR`;
    const r = res.rec;
    if (r.mode === 'single') {
      answerBig = `${tonLabel(r.size)} AC`;
      answerShort = r.ans.byHeat ? `${tonLabel(r.size)}, rated ${r.ans.ratedC}°C` : tonLabel(r.size);
      if (r.altPair) {
        answerMore = `Or two smaller ACs: ${tonLabel(r.altPair.sizes[0])} + ${tonLabel(r.altPair.sizes[1])}, ${formatINR(r.altPair.price)} together.`;
      }
    } else if (r.mode === 'pair') {
      answerBig = 'Two ACs';
      answerShort = `${tonLabel(r.pair.sizes[0])} + ${tonLabel(r.pair.sizes[1])}`;
      answerSub = answerShort;
    } else {
      answerBig = TR;
      answerShort = `${TR}: central system`;
      answerMore = 'More than split ACs can handle well in one room. A cassette, ductable or VRV system will cool it evenly.';
    }
    if (r.mode !== 'team') chart = keepUpRows(res);
    waText = [
      'Hi, I used the AC calculator on citicool.in.',
      `Room: ${ROOM_LABEL[applied.room]}, ${res.l} x ${res.w} ft (${res.area} sq ft), ${FLOOR_WORDS[applied.floor]}, ${SUN_WORDS[applied.sun]}, ${applied.people} people`,
      `City: ${res.city.label}`,
      `Load: ${TR} ${gov.words}`,
      `Suggested: ${answerShort}`,
      ...(r.mode === 'single' && r.rec ? [`Recommended: ${r.rec.model.title} (${r.rec.idu}), ${formatINR(r.rec.price)}`] : []),
      'Please help me choose.',
    ].join('\n');
  }
  const waHref = whatsAppLink(waText || 'Hi, I need help choosing the right AC size.');
  const onWhatsApp = (source) => () => track(FUNNEL.WHATSAPP_HANDOFF, { source: `calculator_${source}` });

  const summary = applied && res
    ? [
      ROOM_LABEL[applied.room], `${res.l} × ${res.w} ft`,
      FLOOR_WORDS[applied.floor], SUN_WORDS[applied.sun],
      `${applied.people} ${applied.people === 1 ? 'person' : 'people'}`, res.city.label,
    ].join(' · ')
    : '';

  const formL = num(form.l, 0, 0, 100);
  const formW = num(form.w, 0, 0, 100);
  const formArea = formL > 0 && formW > 0 ? Math.round(formL * formW) : null;
  const rt = ROOM_TYPES[form.room] || ROOM_TYPES.bedroom;
  const detailedForm = form.mode === 'detailed';
  const calcLabel = onResult ? 'Update result' : 'Calculate AC size';
  const formCity = cityFor(form.city);
  const formPeak = HL.climateFor(formCity.station).peakC;
  const picks = res && res.rec.mode === 'single' ? res.rec.picks : [];
  const bestBuy = res && res.rec.mode === 'single' ? res.rec.rec : null;
  const pairBuy = res && res.rec.mode === 'pair' && res.rec.pair.inStock ? res.rec.pair : null;
  const addPair = () => addPairToCart(pairBuy, 'calculator_sticky');
  const fixedGap = res && res.rec.mode === 'single' ? res.rec.shortFixed || res.rec.noFixed : null;
  const compareCols = picks.length + (fixedGap ? 1 : 0);
  const addBestBuy = () => {
    if (!bestBuy.inStock) {
      navigate(`/request-unit?model=${bestBuy.model.slug}&kit=${bestBuy.kit}`);
      return;
    }
    addToCart(bestBuy.model.id, bestBuy.kit, 1);
    track(FUNNEL.ADD_TO_CART, { model: bestBuy.model.id, kit: bestBuy.kit, qty: 1, value: bestBuy.price, source: 'calculator_sticky' });
    openCart();
  };

  return (
    <>
      <Helmet>
        <title>{CALC_SEO.title}</title>
        <meta name="description" content={CALC_SEO.meta} />
      </Helmet>

      <main className="mx-auto max-w-6xl px-4 pt-20 pb-28 lg:pt-24 lg:pb-16">

        {!onResult && (
          <>
            <div className="mb-4 overflow-hidden rounded-xl">
              <CredentialRibbon />
            </div>
            <picture className="mb-4 block w-full overflow-hidden rounded-xl sm:mb-6">
              <img
                src={BANNER.src}
                srcSet={BANNER.srcSet}
                sizes="(min-width: 1152px) 1120px, 100vw"
                alt={BANNER.alt}
                width={BANNER.width}
                height={BANNER.height}
                loading="eager"
                fetchPriority="high"
                decoding="async"
                className="w-full"
              />
            </picture>
            <nav className="text-[11px] text-gray-500 sm:text-xs">
              <Link to="/daikin-ac-price-list-delhi" className="hover:text-blue-600">All Daikin ACs</Link>
              <span className="mx-1.5">›</span>
              <span className="text-gray-700">AC size calculator</span>
            </nav>
            <h1 className="mt-1 text-xl font-semibold text-gray-900 sm:text-3xl">{CALC_SEO.h1}</h1>
            <p className="mt-1 max-w-2xl text-xs leading-relaxed text-gray-600 sm:mt-2 sm:text-sm">{CALC_SEO.intro}</p>
            <ul className="mt-3 flex flex-wrap gap-1.5">
              {[
                [ClipboardCheck, "Daikin's heat-load method"],
                [MapPin, `${STATION_COUNT} Indian cities`],
                [Zap, 'Live price and stock'],
              ].map(([Icon, text]) => (
                <li key={text} className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-1.5 text-[11px] font-semibold text-blue-700 sm:text-xs">
                  <Icon className="h-3.5 w-3.5" aria-hidden="true" /> {text}
                </li>
              ))}
            </ul>
          </>
        )}

        {onResult && (
          <div className="flex items-center gap-2.5 rounded-xl border border-gray-200 bg-gray-50 py-2 pl-3 pr-2">
            <svg viewBox="0 0 24 24" className="h-[18px] w-[18px] shrink-0 text-gray-500" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d="M4 7h10M18 7h2M4 17h4M12 17h8" /><circle cx="16" cy="7" r="2" /><circle cx="10" cy="17" r="2" />
            </svg>
            <p className="min-w-0 flex-1 text-xs leading-snug text-gray-600">{summary}</p>
            <button type="button" onClick={openEdit} aria-expanded={editing}
              className="shrink-0 rounded-full border border-blue-600 bg-white px-4 py-2.5 text-xs font-medium leading-none text-blue-700">
              {editing ? 'Close' : 'Edit'}
            </button>
          </div>
        )}

        {/* ── the questions ── */}
        {showForm && (
          <div className="lg:flex lg:items-start lg:gap-6">
          <section aria-label="Room details" className="mt-4 w-full min-w-0 rounded-2xl border border-gray-200 bg-white p-4 sm:p-6 lg:flex-1">
            <div className="flex flex-col gap-6">
              <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Calculator mode">
                <Pill on={!detailedForm} onClick={() => set({ mode: 'quick' })}>Quick</Pill>
                <Pill on={detailedForm} onClick={() => set({ mode: 'detailed' })}>Detailed</Pill>
                <span className="text-xs text-gray-500">
                  {detailedForm ? 'Every line of the heat-load sheet, for engineers and architects' : 'Detailed: every line of the heat-load sheet'}
                </span>
              </div>

              <Step n={1} title="Which room?">
                <div className="grid grid-cols-3 gap-2 lg:grid-cols-6">
                  {ROOMS.map(([k, label, Icon]) => (
                    <Tile key={k} on={form.room === k} label={label}
                      onClick={() => set({ room: k, people: ROOM_TYPES[k].people, lw: '', eq: '', glass: '' })}>
                      <Icon className={`h-7 w-7 ${form.room === k ? '' : 'text-blue-600'}`} strokeWidth={1.7} aria-hidden="true" />
                    </Tile>
                  ))}
                </div>
              </Step>

              <div className="grid gap-6 lg:grid-cols-2">
                <Step n={2} title="Room size">
                  <div className="flex items-end gap-2.5">
                    <Field id="calc-l" label="Length (feet)">
                      <input id="calc-l" type="number" inputMode="decimal" min="4" max="100" placeholder="12"
                        value={form.l} onChange={(e) => set({ l: e.target.value })} className={inputCls} />
                    </Field>
                    <span aria-hidden="true" className="pb-3 text-gray-500">×</span>
                    <Field id="calc-w" label="Width (feet)">
                      <input id="calc-w" type="number" inputMode="decimal" min="4" max="100" placeholder="14"
                        value={form.w} onChange={(e) => set({ w: e.target.value })} className={inputCls} />
                    </Field>
                  </div>
                  <p className={`mt-2 inline-flex rounded-full px-2.5 py-1 text-xs ${formArea ? 'bg-blue-50 font-semibold text-blue-700' : 'text-gray-500'}`}>
                    {formArea ? `= ${formArea.toLocaleString('en-IN')} sq ft` : 'Measure wall to wall, in feet.'}
                  </p>
                </Step>

                <Step n={3} title="Which floor?"
                  hint={form.floor === 'top' ? 'The roof slab bakes all day. Top floors need the most cooling.' : 'Top floor means the roof is right above the room.'}
                  hintClass={form.floor === 'top' ? 'text-orange-700' : 'text-gray-500'}>
                  <div className="grid grid-cols-3 gap-2">
                    {FLOORS.map(([k, label, lit]) => (
                      <Tile key={k} on={form.floor === k} label={label} onClick={() => set({ floor: k })}>
                        <FloorIcon lit={lit} on={form.floor === k} />
                      </Tile>
                    ))}
                  </div>
                </Step>
              </div>

              <Step n={4} title="When does sun hit the main window?" hint="Afternoon sun (a west-facing window) adds the most heat.">
                <div className="grid grid-cols-4 gap-2">
                  {SUNS.map(([k, label, short, Icon, tint]) => (
                    <Tile key={k} on={form.sun === k} onClick={() => set({ sun: k })} className="lg:flex-row lg:gap-2"
                      label={<><span className="lg:hidden">{short}</span><span className="hidden lg:inline">{label}</span></>}>
                      <Icon className={`h-6 w-6 ${tint}`} strokeWidth={1.8} aria-hidden="true" />
                    </Tile>
                  ))}
                </div>
              </Step>

              <Step n={5} title="People and city">
                <div className="flex flex-wrap items-end gap-3">
                  <div>
                    <p className="text-xs text-gray-600">People</p>
                    <div className="mt-1 inline-flex items-center overflow-hidden rounded-lg border border-gray-300">
                      <button type="button" aria-label="Fewer people" onClick={() => set({ people: Math.max(1, form.people - 1) })}
                        className="h-11 w-11 text-lg text-gray-600 hover:bg-gray-50">−</button>
                      <span aria-live="polite" className="h-11 min-w-[2.75rem] border-x border-gray-300 text-center text-[15px] font-medium leading-[2.75rem] tabular-nums">{form.people}</span>
                      <button type="button" aria-label="More people" onClick={() => set({ people: Math.min(40, form.people + 1) })}
                        className="h-11 w-11 text-lg text-gray-600 hover:bg-gray-50">+</button>
                    </div>
                  </div>
                  <div className="min-w-[11rem] flex-1">
                    <label htmlFor="calc-city" className="block text-xs text-gray-600">City</label>
                    <select id="calc-city" value={form.city} onChange={(e) => set({ city: e.target.value })} className={`${inputCls} mt-1 bg-white`}>
                      <optgroup label="Delhi NCR">
                        {NCR_CITIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                      </optgroup>
                      <optgroup label="Rest of India">
                        {INDIA_CITIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                      </optgroup>
                    </select>
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-2.5 rounded-xl bg-orange-50 px-3 py-2.5">
                  <Thermometer className="h-5 w-5 shrink-0 text-orange-600" aria-hidden="true" />
                  <p className="text-xs leading-snug text-orange-900">
                    <b className="font-bold">{degC(formPeak)}</b> on {formCity.label}&apos;s hottest afternoon. We size for that day, not an average one.
                    <span className="text-orange-800"> Not listed? Pick the nearest city.</span>
                  </p>
                </div>
              </Step>

              {detailedForm && (
                <div className="border-t border-gray-200 pt-4">
                  <p className="text-sm font-semibold text-gray-900">Engineering inputs</p>
                  <p className="mt-0.5 text-xs text-gray-500">Filled in from the room type. Change any of them.</p>
                  <div className="mt-3 grid grid-cols-2 gap-3 lg:grid-cols-3">
                    <Field id="calc-ch" label="Ceiling height (ft)">
                      <input id="calc-ch" type="number" min="8" max="25" value={form.ch} onChange={(e) => set({ ch: e.target.value })} className={inputCls} />
                    </Field>
                    <Field id="calc-t" label="Room temperature (°C)">
                      <input id="calc-t" type="number" min="18" max="28" value={form.t} onChange={(e) => set({ t: e.target.value })} className={inputCls} />
                    </Field>
                    <Field id="calc-glass" label="Window glass">
                      <select id="calc-glass" value={form.glass || rt.glassType} onChange={(e) => set({ glass: e.target.value })} className={`${inputCls} bg-white`}>
                        <option value="single">Single glazed</option>
                        <option value="double">Double glazed</option>
                      </select>
                    </Field>
                    <Field id="calc-ga" label="Glass area (sq ft)">
                      <input id="calc-ga" type="number" min="0" max="500" placeholder="Auto" value={form.ga} onChange={(e) => set({ ga: e.target.value })} className={inputCls} />
                    </Field>
                    <Field id="calc-roof" label="Roof (top floor)">
                      <select id="calc-roof" value={form.roof} onChange={(e) => set({ roof: e.target.value })} className={`${inputCls} bg-white`}>
                        <option value="">Bare RCC slab</option>
                        <option value="1">Insulated</option>
                      </select>
                    </Field>
                    <Field id="calc-lw" label="Lighting (W per sq ft)">
                      <input id="calc-lw" type="number" min="0" max="10" step="0.1" value={form.lw === '' ? rt.lightW : form.lw} onChange={(e) => set({ lw: e.target.value })} className={inputCls} />
                    </Field>
                    <Field id="calc-eq" label="Equipment (kW)">
                      <input id="calc-eq" type="number" min="0" max="100" step="0.1" value={form.eq === '' ? rt.eqKW : form.eq} onChange={(e) => set({ eq: e.target.value })} className={inputCls} />
                    </Field>
                    <Field id="calc-sf" label="Safety margin (%)">
                      <input id="calc-sf" type="number" min="0" max="20" value={form.sf} onChange={(e) => set({ sf: e.target.value })} className={inputCls} />
                    </Field>
                    <Field id="calc-rate" label="Electricity (₹ per unit)">
                      <input id="calc-rate" type="number" min="1" max="20" step="0.1" placeholder={`₹${tariffFor(form.room)}`}
                        value={form.rate} onChange={(e) => set({ rate: e.target.value })} className={inputCls} />
                    </Field>
                  </div>
                </div>
              )}

              {missing.length > 0 && (
                <p role="alert" className="text-xs font-medium text-red-600">Please add: {missing.join(', ')}.</p>
              )}

              <div className="hidden lg:block">
                <button type="button" onClick={calculate}
                  className="h-12 w-full rounded-xl bg-blue-700 text-[15px] font-semibold text-white transition hover:bg-blue-800">
                  {calcLabel}
                </button>
                <p className="mt-2 text-center text-xs text-gray-500">Free. No sign-up.</p>
              </div>
            </div>
          </section>
          {!onResult && (
            <aside className="mt-4 hidden w-80 shrink-0 flex-col gap-4 lg:flex">
              <div className="rounded-2xl bg-blue-50 p-5">
                <p className="text-sm font-bold text-gray-900">What you get</p>
                <ul className="mt-3.5 space-y-3.5">
                  {[
                    [ChartLine, 'text-blue-700', 'The exact heat load of your room, in TR and BTU/hr'],
                    [Thermometer, 'text-orange-600', "The smallest AC that still keeps up on your city's hottest day"],
                    [Check, 'text-green-700', "Daikin ACs that fit, at today's price, in-stock units first"],
                    [IndianRupee, 'text-blue-700', 'What each one costs to run, and over 5 years'],
                  ].map(([Icon, tint, text]) => (
                    <li key={text} className="flex items-start gap-3">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white">
                        <Icon className={`h-4 w-4 ${tint}`} strokeWidth={2.2} aria-hidden="true" />
                      </span>
                      <span className="text-[13px] leading-snug text-gray-700">{text}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-2xl border border-green-200 bg-green-50 p-5">
                <p className="text-sm font-bold text-gray-900">Rather talk to an engineer?</p>
                <p className="mt-1 text-xs leading-relaxed text-gray-700">Sales and service under one roof, cooling Delhi since {BUSINESS.since}.</p>
                <div className="mt-3">
                  <TeamButtons waHref={waHref} onWhatsApp={onWhatsApp('form_aside')} />
                </div>
              </div>
            </aside>
          )}
          </div>
        )}

        {/* ── the result: the answer, one question, one best buy; the rest folds away ── */}
        {res && (
          <div className="mt-4 flex flex-wrap items-start gap-4 lg:gap-6">

            <div className="flex min-w-0 flex-[1_1_300px] flex-col gap-4">
              <section className="rounded-2xl bg-blue-700 p-4 text-white sm:p-5">
                <p className="text-[13px] text-blue-200">{res.rec.mode === 'single' ? 'Your room needs a' : 'Your room needs'}</p>
                <h1 className="mt-0.5 text-[38px] font-extrabold leading-[1.05] tracking-tight sm:text-5xl">{answerBig}</h1>
                {answerSub && <p className="mt-1 text-lg font-bold text-white">{answerSub}</p>}
                <p className="mt-2.5 flex items-center gap-1.5 text-xs text-blue-100">
                  <Thermometer className="h-4 w-4 shrink-0 text-orange-300" aria-hidden="true" />
                  Sized for {res.city.label}&apos;s hottest days, {degC(res.need.peakC)}
                </p>
                {answerMore && <p className="mt-2 text-xs leading-relaxed text-blue-100">{answerMore}</p>}
              </section>

              {res.rec.mode !== 'team' && (
                <section aria-label="Hours a day">
                  <p className="text-sm font-bold text-gray-900">How many hours a day will you run it?</p>
                  <div className="mt-2 grid grid-cols-4 gap-1.5" role="group" aria-label="Hours a day">
                    {HOURS.map((h) => {
                      const on = applied.hours === h;
                      return (
                        <button key={h} type="button" aria-pressed={on} onClick={() => refine({ hours: h })}
                          className={`h-11 rounded-xl text-sm transition ${
                            on ? 'bg-blue-600 font-bold text-white' : 'border border-gray-300 bg-white font-medium text-gray-700 hover:border-blue-400'
                          }`}>
                          {h === 16 ? '16+' : h} hrs
                        </button>
                      );
                    })}
                  </div>
                  <button type="button" aria-pressed={applied.heating} onClick={() => refine({ heating: !applied.heating })}
                    className={`mt-2 inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-xs ${
                      applied.heating ? 'bg-orange-50 font-semibold text-orange-800' : 'text-gray-600 hover:text-blue-700'
                    }`}>
                    <Sun className="h-4 w-4" aria-hidden="true" />
                    {applied.heating ? 'Heating in winter: on' : 'Need heating in winter too?'}
                  </button>
                </section>
              )}
            </div>

            <div id="options" className="flex min-w-0 flex-[999_1_520px] scroll-mt-24 flex-col gap-5">
              {res.rec.mode === 'single' && res.rec.rec && <BestBuy r={res.rec} peakC={res.need.peakC} />}

              {res.rec.mode === 'single' && compareCols > 1 && (
                <section aria-label="Compare">
                  <div className="flex items-center justify-between gap-3">
                    <h2 className="text-base font-bold text-gray-900">{compareCols === 3 ? 'Or compare all three' : 'Or compare'}</h2>
                    <Link to={`/compare-ac?ac=${picks.map((v) => v.model.id).join(',')}`} data-testid="calc-compare-these"
                      className="shrink-0 rounded-md border border-blue-600 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-50">
                      Compare these
                    </Link>
                  </div>
                  <p className="mt-0.5 text-xs text-gray-500">
                    {applied.hours === 16 ? '16+' : applied.hours} hrs a day · electricity at ₹{res.rate % 1 ? res.rate.toFixed(2) : res.rate} a unit
                  </p>
                  <div className={`mt-4 grid gap-2 ${compareCols >= 4 ? 'grid-cols-2 sm:grid-cols-4' : compareCols === 3 ? 'grid-cols-3' : 'grid-cols-2'}`}>
                    {fixedGap && (
                      <div className="flex min-w-0 flex-col rounded-xl border border-dashed border-gray-300 bg-gray-50 px-2 pb-2 pt-3.5 text-center">
                        {fixedGap.image && (
                          <img src={fixedGap.image} alt="" width="160" height="80" loading="lazy"
                            onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = IMG_FALLBACK; }}
                            className="mx-auto h-14 w-full object-contain opacity-40 grayscale sm:h-16" />
                        )}
                        <p className="mt-1.5 text-[13px] font-extrabold text-gray-500 sm:text-sm">Non-inverter</p>
                        <p className="mt-2 text-xs font-bold text-red-700">Can&apos;t cool this room at {degC(res.need.peakC)}</p>
                        <p className="mt-1.5 text-[11px] leading-snug text-gray-500">
                          {fixedGap.size === res.rec.size ? 'The' : 'Even the biggest,'} {tonLabel(fixedGap.size)} gives only {fixedGap.givesTR.toFixed(2)} TR in that heat. You need {res.need.loadTR.toFixed(2)} TR.
                        </p>
                      </div>
                    )}
                    {picks.map((v) => <CompareCol key={v.key} v={v} size={res.rec.size} />)}
                  </div>
                </section>
              )}

              {res.rec.mode === 'pair' && (
                <>
                  <PairBuy pair={res.rec.pair} need={res.need} hours={applied.hours} near={res.rec.near} />
                  {res.rec.near && <NearSingle v={res.rec.near} need={res.need} />}
                </>
              )}

              {res.rec.mode === 'team' && (
                <section className="flex flex-col gap-2.5 rounded-2xl border border-blue-200 bg-blue-50 p-4">
                  <p className="text-sm font-bold text-gray-900">Talk to our team for an expert opinion</p>
                  <p className="text-xs leading-relaxed text-gray-700">
                    We design and install cassette, ductable and VRV systems across Delhi NCR. Send the room details and our engineers will tell you what fits and what it costs.
                  </p>
                  <TeamButtons waHref={waHref} onWhatsApp={onWhatsApp('team')} />
                </section>
              )}

              <ConsultCard mode={res.rec.mode} area={res.area} type={BUSINESS_ROOMS.includes(applied.room) ? 'commercial' : 'home'} />

              <div className="flex flex-col gap-2">
                <details id="working" className="group scroll-mt-24 rounded-2xl border border-blue-200 bg-white" open={res.detailed}>
                  <summary className="flex cursor-pointer list-none items-center gap-3 rounded-2xl bg-blue-50 p-3.5 group-open:rounded-b-none [&::-webkit-details-marker]:hidden">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-blue-700 ring-1 ring-blue-100">
                      <ChartLine className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-bold text-gray-900">
                        {res.rec.mode === 'single' ? `Why ${tonLabel(res.rec.size)}? The full calculation` : 'The full calculation'}
                      </span>
                      <span className="mt-0.5 block text-[11px] leading-snug text-gray-600">
                        Where the heat comes from · which ACs keep up{res.rec.mode === 'single' && picks.length > 1 ? ' · 5-year cost' : ''}
                      </span>
                    </span>
                    <span className="shrink-0 text-xs font-semibold text-blue-700 group-open:hidden">Open</span>
                    <svg viewBox="0 0 20 20" className="h-4 w-4 shrink-0 text-blue-700 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <path d="M5 8l5 5 5-5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </summary>
                  <div className="p-3 sm:p-4">
                    <Calculation res={res} applied={applied} chart={chart} />
                  </div>
                </details>
                {res.rec.mode === 'single' && res.rec.count > 1 && collectionFor(res.rec.size) && (
                  <Link to={`/${collectionFor(res.rec.size).slug}`}
                    className="flex items-center justify-between rounded-xl border border-gray-200 bg-white p-3.5 text-sm font-semibold text-gray-900 hover:border-blue-300">
                    All {res.rec.count} ACs in {tonLabel(res.rec.size)} <span aria-hidden="true" className="text-gray-400">›</span>
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}

        {/* The guide, FAQ and tables (also pre-rendered for Google) sit under the
            questions. On a result they would only push the answer down. */}
        {!onResult && <Guide guide={guide} cities={cities} faqs={faqs} how={how} note={note} />}
        {onResult && (
          <p className="mt-10 text-center text-sm">
            <Link to="/ac-calculator" className="font-semibold text-blue-600">AC size guide and FAQ ›</Link>
          </p>
        )}

        {/* Sticky bar on phones, like the product page's buy bar. The floating
            call and WhatsApp buttons are hidden on this page on phones
            (lib/productPath.js), so nothing sits on top of it. */}
        <div className="fixed inset-x-0 bottom-0 z-50 flex items-center gap-2.5 border-t border-gray-200 bg-white px-3 pb-[max(0.625rem,env(safe-area-inset-bottom))] pt-2.5 shadow-[0_-2px_12px_rgba(0,0,0,0.08)] lg:hidden">
          <a href={waHref} target="_blank" rel="noopener noreferrer" onClick={onWhatsApp('sticky_bar')}
            aria-label="Chat on WhatsApp"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-green-200 bg-green-50 text-green-700">
            <ChatIcon />
          </a>
          {showForm ? (
            <button type="button" onClick={calculate}
              className="h-11 flex-1 rounded-xl bg-blue-700 px-4 text-sm font-semibold text-white">
              {calcLabel}
            </button>
          ) : pairBuy ? (
            <>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[11px] text-gray-600">Best buy · {answerShort}</p>
                <p className="truncate text-base font-extrabold text-gray-900">{formatINR(pairBuy.price)}</p>
              </div>
              <button type="button" onClick={addPair}
                className="h-11 shrink-0 rounded-xl bg-blue-600 px-4 text-sm font-bold text-white">
                Add both to cart
              </button>
            </>
          ) : bestBuy ? (
            <>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[11px] text-gray-600">Best buy · {tonLabel(bestBuy.size)} {bestBuy.typeLabel.replace(' inverter', '')}</p>
                <p className="truncate text-base font-extrabold text-gray-900">{formatINR(bestBuy.price)}</p>
              </div>
              <button type="button" onClick={addBestBuy}
                className="h-11 shrink-0 rounded-xl bg-blue-600 px-5 text-sm font-bold text-white">
                {bestBuy.inStock ? 'Add to cart' : 'Request'}
              </button>
            </>
          ) : (
            <>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[11px] text-gray-600">Your room needs</p>
                <p className="truncate text-base font-bold text-gray-900">{answerShort}</p>
              </div>
              <button type="button" onClick={openEdit}
                className="h-11 shrink-0 rounded-xl border-2 border-blue-700 px-4 text-sm font-semibold text-blue-700">
                Edit room
              </button>
            </>
          )}
        </div>
      </main>
    </>
  );
}

// ─── below the first screen of the result ─────────────────────────────────

const DRIVER_LOOK = {
  walls: { Icon: House, label: 'Roof, walls and floors', icon: 'bg-blue-50 text-blue-700', bar: 'bg-blue-700' },
  people: { Icon: Lightbulb, label: 'People, lights, gadgets', icon: 'bg-sky-50 text-sky-600', bar: 'bg-blue-400' },
  sun: { Icon: Sun, label: 'Sun through the window', icon: 'bg-amber-50 text-amber-600', bar: 'bg-amber-500' },
  air: { Icon: Wind, label: 'Fresh air coming in', icon: 'bg-cyan-50 text-cyan-700', bar: 'bg-cyan-600' },
};

// "See the calculation": everything behind the answer, folded away by default.
function Calculation({ res, applied, chart }) {
  const n = res.need;
  const cl = res.climate;
  const st = res.city.station;
  const gov = governing(n);
  const govSeason = gov.season === 'summer' ? n.hl.summer : n.hl.monsoon;
  const drivers = [...heatDrivers(govSeason)].sort((a, b) => b.pct - a.pct);
  // The roof on a top floor: its share of the heat, and what insulation would save.
  const parts = drivers.reduce((s, d) => s + d.v, 0);
  const roofPct = parts > 0 ? Math.round((govSeason.lines.roof / parts) * 100) : 0;
  const roofCut = Math.round((1 - HL.U.roofInsulated / HL.U.roofBare) * 10) * 10;
  const roofTip = applied.floor === 'top' && !res.room.roofInsulated && roofPct >= 10;
  const fmt = (x) => Math.round(x).toLocaleString('en-IN');
  const peakSource = cl.peakMeasured
    ? `the hottest day of an average year at ${st.imdStation || st.name} (IMD, 1991–2020)`
    : `the IS 7896 design temperature for ${st.name} (${degC(st.db)}) plus 3°C, as IMD does not publish its hottest-day average`;
  const rateWords = res.rate === tariffFor(applied.room)
    ? (BUSINESS_ROOMS.includes(applied.room) ? 'a typical commercial rate' : 'a typical rate once AC use pushes a home into the higher slabs')
    : 'your rate';

  const picks = res.rec.mode === 'single' ? res.rec.picks : [];
  const five = picks.length > 1 ? [...picks].sort((x, y) => x.fiveYear - y.fiveYear) : [];
  const fiveMax = five.length ? Math.max(...five.map((p) => p.fiveYear)) : 1;
  // The BEE star label (units a year for 1,600 hours) scaled to these hours: a check anyone can read off the sticker.
  const labelOf = picks.find((p) => p.recommended && p.model.annualKwh);
  const labelHrs = applied.hours * 30 * res.city.mo;
  const label = labelOf ? { v: labelOf, hrs: labelHrs, bill: Math.round((labelOf.model.annualKwh * labelHrs / 1600) * res.rate) } : null;
  const card = 'rounded-xl border border-gray-200 bg-white p-3.5';
  const h = 'text-[15px] font-bold text-gray-900';

  return (
    <div className="flex flex-col gap-3">
      <StatTiles need={n} />

      {chart && <div className={card}><KeepUpChart chart={chart} /></div>}

      <div className={card}>
        <p className={h}>Where the heat comes from</p>
        <ul className="mt-3 flex flex-col gap-3">
          {drivers.map((d) => {
            const look = DRIVER_LOOK[d.key];
            return (
              <li key={d.key} className="flex items-center gap-2.5">
                <span className={`flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-lg ${look.icon}`}>
                  <look.Icon className="h-[18px] w-[18px]" aria-hidden="true" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex justify-between gap-2 text-xs text-gray-800">
                    <span>{look.label}</span><b className="tabular-nums">{d.pct}%</b>
                  </div>
                  <div className="mt-1 h-1.5 rounded-full bg-gray-100">
                    <div className={`h-1.5 rounded-full ${look.bar}`} style={{ width: `${d.pct}%` }} />
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
        {roofTip && (
          <p className="mt-3 rounded-lg bg-amber-50 p-2.5 text-xs leading-relaxed text-amber-900">
            The roof alone is {roofPct}% of this room&apos;s heat. Insulating it would cut that by about {roofCut}%.
          </p>
        )}
      </div>

      {five.length > 0 && (
        <div className={card}>
          <p className={h}>Price plus 5 years of electricity</p>
          <p className="mt-0.5 text-xs text-gray-500">
            {applied.hours} hrs a day, {res.city.mo} months a year, at ₹{res.rate % 1 ? res.rate.toFixed(2) : res.rate} a unit, {rateWords}.
          </p>
          <ul className="mt-3 flex flex-col gap-3">
            {five.map((p) => (
              <li key={p.key}>
                <div className="flex items-baseline justify-between gap-2 text-xs">
                  <span className="min-w-0 font-semibold text-gray-900">
                    {p.typeLabel} · {tonLabel(p.size)} · {p.model.series}
                    {p.recommended && <span className="ml-1.5 rounded-full bg-green-600 px-1.5 py-0.5 text-[10px] font-bold text-white">Best buy</span>}
                  </span>
                  <b className={`shrink-0 tabular-nums ${p.recommended ? 'text-green-700' : 'text-gray-900'}`}>{formatINR(p.fiveYear)}</b>
                </div>
                <div className="mt-1 flex h-2 overflow-hidden rounded-full bg-gray-100">
                  <div className="bg-blue-700" style={{ width: `${(p.price / fiveMax) * 100}%` }} title={`Price ${formatINR(p.price)}`} />
                  <div className="bg-blue-300" style={{ width: `${((p.yearly * 5) / fiveMax) * 100}%` }} title={`5 years of power ${formatINR(p.yearly * 5)}`} />
                </div>
                <p className="mt-0.5 text-[11px] tabular-nums text-gray-500">{formatINR(p.price)} + {formatINR(p.yearly * 5)} power</p>
              </li>
            ))}
          </ul>
          <div className="mt-3 flex gap-4 text-[11px] text-gray-600">
            <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-blue-700" />Price</span>
            <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-blue-300" />5 years of power</span>
          </div>
          {label && (
            <p className="mt-3 rounded-lg bg-gray-50 p-2.5 text-[11px] leading-relaxed text-gray-600">
              Star-label check: the {label.v.model.series}&apos;s BEE label says {fmt(label.v.model.annualKwh)} units a year for 1,600 hours.
              At your {fmt(label.hrs)} hours a year that is about {formatINR(label.bill)}; our figure for this room is {formatINR(label.v.yearly)}.
            </p>
          )}
        </div>
      )}

      <div className={card}>
        <p className={h}>How we worked it out</p>
        <h3 className="mt-2.5 text-[13px] font-semibold text-gray-900">1. The weather in {res.city.label}</h3>
        <p className="mt-0.5 text-xs leading-relaxed text-gray-600">
          Hottest afternoon: {degC(n.peakC)}, {peakSource}. Monsoon: {degC(st.mcdb)} with a {degC(st.wb)} wet bulb (IS 7896).
          Day-to-night swing in the hottest month: {degC(cl.dailyRangeC)}. Room held at {res.targetTemp}°C and 55% humidity.
        </p>
        <h3 className="mt-3 text-[13px] font-semibold text-gray-900">2. The heat load, the Daikin way</h3>
        <p className="mt-0.5 text-xs leading-relaxed text-gray-600">
          {degC(n.peakC)} afternoon: {n.summerTR.toFixed(2)} TR. Monsoon: {n.monsoonTR.toFixed(2)} TR. We size for the bigger, {n.loadTR.toFixed(2)} TR
          ({fmt(n.loadTR * 12000)} BTU/hr). Rooms around: the average case, half the inside walls, floor and ceiling next to rooms that are not air-conditioned.
        </p>
        {res.detailed && (
          <div className="mt-3">
            <div className="grid grid-cols-[minmax(0,1fr)_auto_auto] gap-x-3 border-b border-gray-200 pb-1.5 text-[11px] text-gray-500">
              <span>BTU/hr</span><span className="w-20 text-right">{degC(n.peakC)}</span><span className="w-20 text-right">Monsoon</span>
            </div>
            {sheetLines(n.hl.summer).map(([label, v], i) => {
              const m = sheetLines(n.hl.monsoon)[i][1];
              if (!(v > 0.5) && !(m > 0.5)) return null;
              return (
                <div key={label} className="grid grid-cols-[minmax(0,1fr)_auto_auto] gap-x-3 border-b border-gray-100 py-1.5 text-xs tabular-nums">
                  <span className="text-gray-700">{label}</span>
                  <span className="w-20 text-right text-gray-900">{fmt(v)}</span>
                  <span className="w-20 text-right text-gray-900">{fmt(m)}</span>
                </div>
              );
            })}
            <div className="grid grid-cols-[minmax(0,1fr)_auto_auto] gap-x-3 pt-2 text-[13px] font-semibold tabular-nums">
              <span>Total</span>
              <span className="w-20 text-right">{n.summerTR.toFixed(2)} TR</span>
              <span className="w-20 text-right">{n.monsoonTR.toFixed(2)} TR</span>
            </div>
            <p className="mt-1.5 text-[11px] text-gray-500">
              {Math.round(n.hl.summer.cfm)} CFM fresh air · {Math.round(res.area / n.loadTR)} sq ft per TR
            </p>
          </div>
        )}
        <h3 className="mt-3 text-[13px] font-semibold text-gray-900">3. The AC&apos;s own limit</h3>
        <p className="mt-0.5 text-xs leading-relaxed text-gray-600">
          Daikin models give full power up to 43°C or 46°C, by series; above that we take off 5% for every °C, using each model&apos;s real kW.
          Only ACs that still cover the room at {degC(n.peakC)}, and in the monsoon, are shown.
        </p>
        {res.rec.mode !== 'team' && !five.length && (
          <>
            <h3 className="mt-3 text-[13px] font-semibold text-gray-900">4. Running cost</h3>
            <p className="mt-0.5 text-xs leading-relaxed text-gray-600">
              {applied.hours} hrs a day, {res.city.mo} months a year, at ₹{res.rate % 1 ? res.rate.toFixed(2) : res.rate} a unit, {rateWords}.
              Worked out from each model&apos;s ISEER rating on your room&apos;s load.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

// ─── the guides, how it works, FAQ (also pre-rendered) ────────────────────

function SizeCell({ c }) {
  const coll = c.size != null ? collectionFor(c.size) : null;
  const chip = `inline-block rounded-lg px-2.5 py-1 text-xs font-bold sm:text-[13px] ${sizeChip(c.size)}`;
  const heat = c.note && c.note.startsWith('Models rated to');
  return (
    <>
      <div className="flex flex-wrap items-center gap-1">
        {coll ? <Link to={`/${coll.slug}`} className={chip}>{c.label}</Link> : <span className={chip}>{c.label}</span>}
        {heat && (
          <span className="inline-flex items-center gap-0.5 whitespace-nowrap rounded-full bg-orange-50 px-1.5 py-0.5 text-[10px] font-semibold text-orange-700 sm:text-[11px]">
            <Thermometer className="h-3 w-3" strokeWidth={2.4} aria-hidden="true" />
            {c.note.replace('Models rated to ', '')}<span className="hidden sm:inline">&nbsp;models</span>
          </span>
        )}
      </div>
      <span className="mt-1 block text-[11px] text-gray-500">{c.trText}</span>
      {c.note && !heat && <span className="block text-[11px] leading-snug text-gray-500">{c.note}</span>}
    </>
  );
}

const HOW_ICONS = [ClipboardCheck, Thermometer, CloudRain, House, Gauge, Check];
const HOW_TINTS = ['text-blue-700', 'text-orange-600', 'text-cyan-700', 'text-blue-700', 'text-blue-700', 'text-green-700'];

function Guide({ guide, cities, faqs, how, note }) {
  const th = 'px-2 py-2.5 align-bottom text-[11px] font-semibold leading-snug text-gray-600 sm:px-3 sm:text-sm';
  return (
    <div className="mt-12 max-w-4xl">
      <section>
        <h2 className="text-lg font-bold text-gray-900 sm:text-xl">AC size by room size</h2>
        <p className="mt-1 text-xs leading-relaxed text-gray-600 sm:text-sm">{note}</p>
        <p className="mt-1.5 text-xs sm:text-sm">
          <Link to="/blog/what-size-ac-do-i-need" className="font-semibold text-blue-600 hover:text-blue-700">The simple answer: what size AC do I need for my room? ›</Link>
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[11px] text-gray-700 sm:text-xs">
          {[1, 1.5, 1.8, 2, 2.5].map((s) => (
            <span key={s} className="inline-flex items-center gap-1.5"><span className={`h-3 w-3 rounded ${sizeChip(s).split(' ')[0]}`} />{tonLabel(s)}</span>
          ))}
          <span className="inline-flex items-center gap-0.5 whitespace-nowrap rounded-full bg-orange-50 px-1.5 py-0.5 font-semibold text-orange-700">
            <Thermometer className="h-3 w-3" strokeWidth={2.4} aria-hidden="true" />46°C<span className="hidden sm:inline">&nbsp;models</span>
          </span>
          <span className="text-gray-500">only models that keep full power to 46°C keep up</span>
        </div>
        <div className="mt-3 overflow-x-auto rounded-2xl border border-gray-200">
          <table className="w-full text-left text-xs sm:text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className={th}>Room</th>
                {GUIDE_COLUMNS.map((c) => {
                  const Icon = c.sun === 'N' ? Cloud : c.floor === 'top' ? House : Sunset;
                  const tint = c.sun === 'N' ? 'text-gray-500' : 'text-orange-600';
                  return (
                    <th key={c.key} scope="col" className={th}>
                      <span className="flex items-start gap-1"><Icon className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${tint}`} aria-hidden="true" />{c.label}</span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {guide.map((row) => (
                <tr key={row.room} className="border-t border-gray-100">
                  <th scope="row" className="whitespace-nowrap px-2 py-2.5 align-top font-semibold text-gray-900 sm:px-3">
                    {row.room}
                    <span className="block text-[11px] font-normal text-gray-500">{row.area} sq ft</span>
                  </th>
                  {row.cells.map((c) => (
                    <td key={c.key} className="px-2 py-2.5 align-top sm:px-3"><SizeCell c={c} /></td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mt-10">
        <h2 className="text-lg font-bold text-gray-900 sm:text-xl">The same room across India</h2>
        <p className="mt-1 text-xs leading-relaxed text-gray-600 sm:text-sm">{CITY_GUIDE_NOTE}</p>
        <div className="mt-3 overflow-x-auto rounded-2xl border border-gray-200">
          <table className="w-full text-left text-xs sm:text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className={th}>City</th>
                <th scope="col" className={th}>Hottest afternoon</th>
                <th scope="col" className={th}>Middle floor</th>
                <th scope="col" className={th}>Top floor</th>
              </tr>
            </thead>
            <tbody>
              {cities.map((row) => {
                const w = Math.max(6, Math.min(100, ((row.peakC - 35) / 12) * 100));
                const bar = row.peakC >= 44 ? 'bg-orange-600' : row.peakC >= 41 ? 'bg-orange-500' : 'bg-orange-300';
                return (
                  <tr key={row.key} className="border-t border-gray-100">
                    <th scope="row" className="px-2 py-2.5 align-top font-semibold text-gray-900 sm:px-3">{row.city}</th>
                    <td className="px-2 py-2.5 align-top sm:px-3">
                      <span className="font-bold tabular-nums text-orange-800">{row.peakText}</span>
                      <span className="mt-1 block h-1.5 w-full max-w-[8rem] rounded-full bg-orange-50">
                        <span className={`block h-1.5 rounded-full ${bar}`} style={{ width: `${w}%` }} />
                      </span>
                      <span className="mt-0.5 block text-[11px] text-gray-500">{row.peakSource}</span>
                    </td>
                    <td className="px-2 py-2.5 align-top sm:px-3"><SizeCell c={row.mid} /></td>
                    <td className="px-2 py-2.5 align-top sm:px-3"><SizeCell c={row.top} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mt-10">
        <h2 className="text-lg font-bold text-gray-900 sm:text-xl">How the calculator works</h2>
        <ul className="mt-3 grid grid-cols-2 gap-2.5 lg:grid-cols-3">
          {how.map((h, i) => {
            const Icon = HOW_ICONS[i] || Check;
            return (
              <li key={h.title} className="rounded-2xl bg-gray-50 p-3.5 sm:p-4">
                <Icon className={`h-5 w-5 ${HOW_TINTS[i] || 'text-blue-700'}`} aria-hidden="true" />
                <h3 className="mt-2 text-[13px] font-bold text-gray-900 sm:text-sm">{h.title}</h3>
                <p className="mt-1 text-[11px] leading-relaxed text-gray-600 sm:text-[13px]">{h.text}</p>
              </li>
            );
          })}
        </ul>
      </section>

      <section className="mt-10">
        <h2 className="text-lg font-bold text-gray-900 sm:text-xl">Frequently asked questions</h2>
        <div className="mt-3">
          {faqs.map((f) => (
            <details key={f.q} className="group border-b border-gray-200">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-3 py-3.5 text-left text-sm font-medium text-gray-900 [&::-webkit-details-marker]:hidden">
                {f.q}
                <svg viewBox="0 0 20 20" className="h-4 w-4 shrink-0 text-gray-400 transition-transform group-open:rotate-180"
                  fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                  <path d="M5 8l5 5 5-5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </summary>
              <p className="pb-4 text-sm leading-relaxed text-gray-600">{f.a}</p>
            </details>
          ))}
        </div>
      </section>
    </div>
  );
}
